import { CaretDownIcon } from '@glu/icons-react';
import { createUseStyles, useDarkMode } from '@glu/theming';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import locale from '@glu/locale';
import { AccessibilityText } from '@glu/utilities-react';
import SelectString from './SelectString';
import GroupLabel from './GroupLabel';
import styles from './SelectField.styles';
import './themeDefaults';

const useStyles = createUseStyles(styles);

const SelectField = ({
  applySelection,
  error,
  dataQa,
  disabled,
  filterEnabled,
  fitParentWidth,
  format,
  handleClose,
  htmlId,
  multiselect,
  open,
  openDropdown,
  placeholder,
  selection,
  setFocusedIndex,
  setPressedKey,
  setSelection,
  showBadges,
  showGroupInSelection,
  showSelected
}) => {
  const dark = useDarkMode();
  const classes = useStyles();

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose();
      event.stopPropagation();
    }
    if (event.key === 'ArrowDown') {
      openDropdown(true);
      if (!filterEnabled) {
        setFocusedIndex(1);
      }
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      applySelection([]);
    }

    // alphanumeric keys
    const isAlphanumericKey = (event.keyCode >= 48 && event.keyCode <= 57)
      || (event.keyCode >= 65 && event.keyCode <= 90);
    if (isAlphanumericKey) {
      openDropdown(true);
      if (!filterEnabled) {
        setPressedKey(event.key);
      }
    }
  };
  const group = selection && selection[0] && selection[0].parentName;

  const getSelectFieldString = () => {
    let selectionString = false;
    const selectionLength = selection.length;
    if (selectionLength) {
      if (selectionLength > showSelected) {
        selectionString = `${selectionLength} ${locale.get('selected')}`;
      } else {
        selectionString = format(selection);
      }
    }
    return selectionString;
  };

  return (
    <div
      className={classnames(
        classes.selectFieldWrapper, { [classes.error]: !!error, [classes.dark]: dark }
      )}
    >
      <AccessibilityText id={`${htmlId}-dropdown-label`}>{locale.get('dropdown')}</AccessibilityText>
      <button
        aria-controls={`${htmlId}-dropdown-panel`}
        aria-expanded={open}
        aria-labelledby={`${htmlId}-select-field-label ${htmlId}-dropdown-label`}
        type="button"
        className={classnames(classes.input, {
          [classes.groupLabel]: showGroupInSelection,
          [classes.disabled]: disabled,
          [classes.dark]: dark
        })}
        disabled={disabled}
        id={`${htmlId}-select-field`}
        data-qa={`${htmlId}-select-field`}
        title={getSelectFieldString() || undefined}
        onClick={() => { openDropdown(!open); }}
        onKeyDown={handleKeyDown}
      >
        {!multiselect && showGroupInSelection && <GroupLabel>{group || locale.get('groupLabel')}</GroupLabel>}
        <CaretDownIcon
          className={classnames(
            classes.caret, { [classes.disabled]: disabled, [classes.dark]: dark }
          )}
        />
      </button>
      <div
        className={classnames(classes.selection, {
          [classes.fitParentWidth]: fitParentWidth,
          [classes.dark]: dark
        })}
        data-qa={dataQa ? `${dataQa}-selection` : `dropdown-${htmlId}-selection`}
      >
        <SelectString
          applySelection={applySelection}
          disabled={disabled}
          format={format}
          placeholder={placeholder}
          selection={selection}
          setSelection={setSelection}
          showBadges={showBadges}
          onClick={() => { if (!disabled) { openDropdown(!open); } }}
          showSelected={showSelected}
        />
      </div>
    </div>
  );
};

SelectField.propTypes = {
  applySelection: PropTypes.func.isRequired,
  dataQa: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  error: PropTypes.string,
  /** UX setting for enabling the filter input in the options dropdown */
  filterEnabled: PropTypes.bool.isRequired,
  fitParentWidth: PropTypes.bool.isRequired,
  format: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  htmlId: PropTypes.string.isRequired,
  /** UX setting to allow multiple selections */
  multiselect: PropTypes.bool.isRequired,
  /** State of the dropdown panel */
  open: PropTypes.bool.isRequired,
  openDropdown: PropTypes.func.isRequired,
  /** Default value for select field's button content. Required to not be empty for A11y */
  placeholder: PropTypes.string.isRequired,
  /** All options currently selected */
  selection: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
    parentName: PropTypes.string
  })).isRequired,
  setFocusedIndex: PropTypes.func.isRequired,
  setPressedKey: PropTypes.func.isRequired,
  setSelection: PropTypes.func.isRequired,
  /** UX setting to display selection as individually clearable items */
  showBadges: PropTypes.bool.isRequired,
  /** Display the group of the selected value in the input */
  showGroupInSelection: PropTypes.bool.isRequired,
  /** Number of options to show in the select field before fallback to "N selected" message* */
  showSelected: PropTypes.number.isRequired
};

SelectField.defaultProps = {
  error: undefined
};

export default SelectField;
